import React, { useState, useEffect } from 'react';
import { 
  Heart, 
  Share2, 
  ChevronLeft, 
  Info, 
  UtensilsCrossed, 
  Clock, 
  Loader2,
  AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';

interface PublicProduct {
  id: string;
  name: string;
  description: string;
  unitPrice: number;
  category: string;
  imageUrl?: string;
  ingredients?: string[];
  orgId: string;
}

interface DigitalMenuProductPageProps {
  id: string;
}

export default function DigitalMenuProductPage({ id }: DigitalMenuProductPageProps) {
  const [product, setProduct] = useState<PublicProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/api/public/items/${encodeURIComponent(id)}`);
        const data = await response.json().catch(() => null);
        if (!response.ok || !data) {
          throw new Error(data?.error || 'Product not found');
        }
        setProduct({
          id: data.id,
          name: data.name,
          description: data.description || '',
          unitPrice: Number(data.unitPrice ?? 0),
          category: data.category || '',
          imageUrl: data.imageUrl || undefined,
          ingredients: Array.isArray(data.ingredients) ? data.ingredients : [],
          orgId: data.orgId
        });
        
        // Check if liked in local storage
        const likedItems = JSON.parse(localStorage.getItem('liked_menu_items') || '[]');
        setIsLiked(likedItems.includes(id));
      } catch (err: any) {
        console.error("Error fetching public product:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const toggleLike = () => {
    if (!product) return;
    const likedItems = JSON.parse(localStorage.getItem('liked_menu_items') || '[]');
    let newLikedItems;
    if (isLiked) {
      newLikedItems = likedItems.filter((itemId: string) => itemId !== product.id);
    } else {
      newLikedItems = [...likedItems, product.id];
    }
    localStorage.setItem('liked_menu_items', JSON.stringify(newLikedItems));
    setIsLiked(!isLiked);
  };

  const shareProduct = () => {
    if (navigator.share) {
      navigator.share({
        title: product?.name,
        text: product?.description,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin opacity-20" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-white text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4 opacity-20" />
        <h1 className="text-xl font-bold mb-2 serif italic">Dish not found</h1>
        <p className="text-sm opacity-50 mb-6">
          {error === 'Product not found' 
            ? `The dish (ID: ${id}) might have been removed from the menu.`
            : `Technical error: ${error}`}
        </p>
        <div className="flex flex-col gap-3">
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-[#141414] text-white rounded-full text-sm font-medium"
          >
            Try Again
          </button>
          <button 
            onClick={() => window.history.back()}
            className="px-6 py-2 border border-black/10 rounded-full text-sm font-medium"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24 lg:flex lg:pb-0 overflow-x-hidden">
      {/* Mobile Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between p-4 lg:hidden">
        <button 
          onClick={() => window.history.back()}
          className="w-10 h-10 bg-white/80 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg transform active:scale-95 transition-transform"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <div className="flex gap-2">
          <button 
            onClick={shareProduct}
            className="w-10 h-10 bg-white/80 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg transform active:scale-95 transition-transform"
          >
            <Share2 className="w-5 h-5" />
          </button>
          <button 
            onClick={toggleLike}
            className={cn(
              "w-10 h-10 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg transform active:scale-95 transition-transform",
              isLiked ? "bg-red-500 text-white" : "bg-white/80"
            )}
          >
            <Heart className={cn("w-5 h-5", isLiked && "fill-current")} />
          </button>
        </div>
      </div>

      {/* Image Section */}
      <div className="relative w-full h-[50vh] lg:h-screen lg:w-1/2 overflow-hidden">
        {product.imageUrl ? (
          <img 
            src={product.imageUrl} 
            alt={product.name} 
            className="w-full h-full object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-full h-full bg-[#141414]/5 flex items-center justify-center">
            <UtensilsCrossed className="w-20 h-20 opacity-10" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent lg:hidden" />
      </div>

      {/* Content Section */}
      <div className="flex-1 p-6 lg:p-12 lg:flex lg:flex-col lg:justify-center max-w-2xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#141414]/40 serif italic">
              {product.category || 'Menu Item'}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#141414]">
              {product.name}
            </h1>
            <div className="text-2xl font-bold text-[#141414]/80 mt-2">
              EGP {(product.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </div>
          </div>

          <div className="h-px w-20 bg-[#141414]/10" />

          <div className="space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
              <Info className="w-4 h-4 opacity-40" />
              Description
            </h3>
            <p className="text-lg text-[#141414]/60 leading-relaxed serif italic">
              {product.description || 'No description provided for this item.'}
            </p>
          </div>

          {product.ingredients && product.ingredients.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                <UtensilsCrossed className="w-4 h-4 opacity-40" />
                Components / Ingredients
              </h3>
              <div className="flex flex-wrap gap-2">
                {product.ingredients.map((ing, idx) => (
                  <span 
                    key={idx}
                    className="px-4 py-2 bg-[#141414]/5 rounded-full text-xs font-medium tracking-wide"
                  >
                    {ing}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Desktop Controls */}
          <div className="hidden lg:flex gap-4 pt-8">
            <button 
              onClick={toggleLike}
              className={cn(
                "flex-1 py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all",
                isLiked ? "bg-red-500 text-white shadow-lg shadow-red-500/20" : "bg-[#141414]/5 text-[#141414]"
              )}
            >
              <Heart className={cn("w-5 h-5", isLiked && "fill-current")} />
              {isLiked ? 'Saved to Favorites' : 'Save Dish'}
            </button>
            <button 
              onClick={shareProduct}
              className="px-6 rounded-2xl bg-[#141414]/5 hover:bg-[#141414]/10 transition-colors"
            >
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      </div>

      {/* Mobile Floating Action Button */}
      <div className="lg:hidden fixed bottom-6 left-6 right-6 z-50">
        <button 
          onClick={toggleLike}
          className={cn(
            "w-full py-4 rounded-[2rem] font-bold flex items-center justify-center gap-3 shadow-2xl transition-all active:scale-95",
            isLiked ? "bg-red-500 text-white" : "bg-[#141414] text-white"
          )}
        >
          <Heart className={cn("w-5 h-5", isLiked && "fill-current")} />
          {isLiked ? 'Saved to Favorites' : 'Save Dish'}
        </button>
      </div>
    </div>
  );
}
